import DescriptionEnum from "../subjective/enums/complaint/DescriptionEnum";
import EtiologyEnum from "../subjective/enums/complaint/EtiologyEnum";
import CharacterEnum from "../subjective/enums/complaint/CharacterEnum";
import AggravatingFactorEnum from "../subjective/enums/complaint/AggravatingFactorEnum";
import FrequencyEnum from "../subjective/enums/complaint/FrequencyEnum";
import FeelEnum from "../subjective/enums/complaint/FeelEnum";
import OnsetEnum from "../subjective/enums/complaint/OnsetEnum";
import TimingEnum from "../subjective/enums/complaint/TimingEnum";
import TimingSensitivityEnum from "../subjective/enums/complaint/TimingSensitivityEnum";
import RelievingFactorsEnum from "../subjective/enums/complaint/RelievingFactorsEnum";
import RedFlagEnum from "../subjective/enums/complaint/RedFlagEnum";
import NatureEnum from "../subjective/enums/complaint/NatureEnum";
import DurationEnum from "../subjective/enums/complaint/DurationEnum";
import ObjectUtil from "../../../../utils/ObjectUtil";

// What the AI compose is allowed to write, grouped by the record section it belongs to.
//
// This is an allowlist on purpose. /emr/compose returns a whole visit-shaped emr_json;
// the panel must never write a key it cannot also show the clinician in plain words,
// with the enum needed to render the value as a label rather than a code. Anything the
// service returns outside this list is surfaced as unmapped (see unmappedComposePaths)
// instead of being silently dropped — that count is the signal to extend this file.
//
// `path` is relative to the visit and is used for BOTH sides: reading the parse out of
// emr_json and writing the accepted value into currentVisit. The two share a shape.
//
// `subjective.complaint.complaint` — the free text — is deliberately absent: it is the
// source of the parse, not a target of it. The Draft tab offers it as its own row, and
// only when the record has no complaint text yet.

const complaint = (key, label, enumType) => ({
    path: `subjective.complaint.${key}`,
    label,
    enum: enumType,
});

const field = (path, label, enumType) => ({path, label, enum: enumType});

export const COMPOSE_SECTIONS = [
    {
        title: "Complaint",
        tab: "subjective",
        fields: [
            complaint("feel", "How does this make you feel?", FeelEnum),
            complaint("historyOfTrauma", "History of Trauma"),
            complaint("etiology", "Etiology", EtiologyEnum),
            complaint("frequency", "Frequency", FrequencyEnum),
            complaint("description", "Description", DescriptionEnum),
            complaint("durationType", "Duration", DurationEnum),
            complaint("onset", "Onset", OnsetEnum),
            complaint("character", "Character", CharacterEnum),
            complaint("timing", "Timing", TimingEnum),
            complaint("timingSensitivity", "Timing Sensitivity", TimingSensitivityEnum),
            complaint("aggravatingFactor", "Aggravating Factors", AggravatingFactorEnum),
            complaint("relievingFactors", "Relieving Factors", RelievingFactorsEnum),
            complaint("pain", "Pain (VAS)"),
            complaint("nature", "Nature", NatureEnum),
            complaint("redFlag", "Red Flags", RedFlagEnum),
            complaint("redFlagNormal", "No Red Flag"),
        ],
    },
    // The sections below mirror what /emr/compose can already return outside the
    // complaint block (see cpt-recommender emr_forms.COMPOSE_FIELDS) — without them
    // those parses surfaced only as an "unmapped" count nobody could review.
    {
        title: "History",
        tab: "subjective",
        fields: [
            field("subjective.medicalHistory.none", "No Significant Medical History"),
            field("subjective.allergy.none", "No Known Allergies"),
        ],
    },
    {
        title: "Objective & Vitals",
        tab: "assessment",
        fields: [
            field("assessment.observation.none", "Observation Normal"),
            field("assessment.posture.none", "Posture Normal"),
            field("assessment.palpation.none", "Palpation Normal"),
            field("assessment.gaitAnalysis.none", "Gait Normal"),
            field("assessment.vitalSigns.temperature", "Temperature (°C)"),
            field("assessment.vitalSigns.pulse", "Pulse (bpm)"),
            field("assessment.vitalSigns.respiration", "Respiration (breaths/min)"),
            field("assessment.vitalSigns.bloodPressureAbove", "Blood Pressure — Systolic"),
            field("assessment.vitalSigns.bloodPressureBelow", "Blood Pressure — Diastolic"),
            field("assessment.vitalSigns.height", "Height (cm)"),
            field("assessment.vitalSigns.weight", "Weight (kg)"),
            // Derived server-side from height+weight; listed so it can be reviewed
            // rather than dropping out as an unmapped path.
            field("assessment.vitalSigns.bmi", "BMI (derived)"),
        ],
    },
    {
        title: "Impression & Plan",
        tab: "treatment",
        fields: [
            field("diagnosis.clinicalImpression", "Clinical Impression"),
            field("treatment.goal.details", "Treatment Goal"),
            field("treatment.response.overallImproveValue", "Overall Improvement (%)"),
            field("treatment.response.romImproveValue", "ROM Improvement (%)"),
            field("treatment.response.painScoreValue", "Pain Score After Treatment"),
            field("treatment.treatmentSchedule.numberOfRecommendedSessions", "Sessions Recommended"),
        ],
    },
];

const MAPPED_PATHS = new Set(
    COMPOSE_SECTIONS.flatMap(section => section.fields.map(field => field.path))
);

// The free text is an input to the parse, not an unmapped output.
const IGNORED_PATHS = new Set(["subjective.complaint.complaint"]);

export const isEmptyComposeValue = value =>
    value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);

// Arrays compare as sets, so a re-parse returning the same options in another order
// is a no-op row rather than a change the clinician has to read again.
const valueKey = value => Array.isArray(value) ? [...value].sort().join("|") : value;

export const formatComposeValue = (field, value) => {
    if (isEmptyComposeValue(value)) return "—";
    const display = v => (field?.enum && field.enum[v] && field.enum[v].name) || String(v);
    if (Array.isArray(value)) return value.map(display).join(", ");
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return display(value);
};

// Existing values first, then whatever the parse adds. Used for multi-value fields, where
// replacing the list wholesale is how a parse silently deletes a finding the clinician
// entered by hand ("dull ache with stiffness" coming back as just Dull).
export const mergeComposeValues = (oldValue, newValue) => {
    const before = Array.isArray(oldValue) ? oldValue : (isEmptyComposeValue(oldValue) ? [] : [oldValue]);
    const after = Array.isArray(newValue) ? newValue : [newValue];
    return [...before, ...after.filter(v => !before.includes(v))];
};

// What a row will actually write, given the clinician's decision.
export const effectiveComposeValue = (row) =>
    row.decision === "keep_both" ? mergeComposeValues(row.oldValue, row.newValue) : row.newValue;

// Only what merging would add. The retained values are already on the record and repeating
// them turns every multi-value row into two near-identical lists to diff by eye.
export const addedComposeValues = (row) => {
    const before = Array.isArray(row.oldValue) ? row.oldValue : [row.oldValue];
    const after = Array.isArray(row.newValue) ? row.newValue : [row.newValue];
    return after.filter(v => !before.includes(v));
};

// A row is destructive only if applying it takes something off the record: a single-value
// overwrite always does, a multi-value one only when the clinician chooses Replace. This is
// what earns the amber accent — if Keep Both is the choice, nothing is being lost and the
// row should look like every other addition.
export const isDestructiveRow = (row) =>
    !!row.overwrites && (!row.multi || row.decision === "replace");

// One row per parsed field that would actually change something on this visit, plus a
// count of the two silent outcomes — the parse agreeing with the record, and the parse
// not returning the field at all. Those are different facts and the review has to say
// which happened, or an empty list reads as "the note was fully understood".
//
// Every row starts undecided (`decision: null`) on purpose: the clinician must take an
// explicit action on each recommendation — Discard, Keep Both (multi-value overwrites) or
// Replace/Add — before Apply unlocks. Defaults that pre-ticked "safe" rows let people save
// without ever reading them (2026-08-17 review).
export const buildComposeRows = (emrJson, visit) => {
    let matched = 0;
    const notReturned = [];

    const sections = COMPOSE_SECTIONS.map(section => {
        const rows = [];
        section.fields.forEach(field => {
            const newValue = ObjectUtil.get(emrJson, field.path);
            if (isEmptyComposeValue(newValue)) {
                notReturned.push(field.label);
                return;
            }
            const oldValue = ObjectUtil.get(visit, field.path);
            if (!isEmptyComposeValue(oldValue) && valueKey(oldValue) === valueKey(newValue)) {
                matched++;
                return;
            }

            const overwrites = !isEmptyComposeValue(oldValue);
            const multi = Array.isArray(oldValue) || Array.isArray(newValue);

            // The parse returned a subset of what is already recorded: merging adds nothing
            // and replacing would only delete. Counted as agreement rather than shown as a
            // decision — a shorter list from one pass is not a correction of a longer one.
            if (overwrites && multi && addedComposeValues({oldValue, newValue}).length === 0) {
                matched++;
                return;
            }

            rows.push({
                field, path: field.path, label: field.label, oldValue, newValue,
                overwrites, multi,
                decision: null,
            });
        });
        return {title: section.title, tab: section.tab, rows};
    }).filter(section => section.rows.length > 0);

    return {sections, matched, notReturned};
};

// Every non-empty leaf path the parse produced, mapped or not. The guideline panel uses
// this as its "captured" signal: compose is called without the record, so emr_json holds
// only what the note itself yielded (plus derived values like BMI).
export const allComposePaths = (emrJson) => {
    const found = [];
    const walk = (node, prefix) => {
        if (node === null || typeof node !== "object" || Array.isArray(node)) return;
        Object.keys(node).forEach(key => {
            const path = prefix ? `${prefix}.${key}` : key;
            const value = node[key];
            if (value !== null && typeof value === "object" && !Array.isArray(value)) {
                walk(value, path);
                return;
            }
            if (isEmptyComposeValue(value) || key === "id") return;
            found.push(path);
        });
    };
    walk(emrJson, "");
    return found;
};

// Everything the parse returned that this panel has no label for. Reported rather than
// applied: writing an unrecognised key into the visit would put a value on the record
// that no one reviewed. Object leaves only — a list of items is a different kind of
// change and is out of scope for the field-level review.
export const unmappedComposePaths = (emrJson) => {
    const found = [];

    const walk = (node, prefix) => {
        if (node === null || typeof node !== "object" || Array.isArray(node)) return;
        Object.keys(node).forEach(key => {
            const path = prefix ? `${prefix}.${key}` : key;
            const value = node[key];
            if (value !== null && typeof value === "object" && !Array.isArray(value)) {
                walk(value, path);
                return;
            }
            if (isEmptyComposeValue(value)) return;
            if (key === "id" || MAPPED_PATHS.has(path) || IGNORED_PATHS.has(path)) return;
            found.push(path);
        });
    };

    walk(emrJson, "");
    return found;
};
